import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import debounce from "lodash.debounce";
import "react-toastify/dist/ReactToastify.css";
import { getData, serverURL } from "../../services/FetchNodeServices";
import defaultImage from "../../images/productItem.jpg";

const ITEMS_PER_PAGE = 10;

const AllProduct = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  /** Fetch data from API */
  const getApiData = async (search = searchTerm, page = currentPage) => {
    try {
      setLoading(true);
      const res = await getData(
        `product/get-all-product?page=${page}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(search)}`
      );

      if (res?.status) {
        setLoading(false);
        setProducts(res?.data || []);
        setTotalPages(res?.totalPages || 1);
      } else {
         setLoading(false);
        toast.error(res.message || "Failed to fetch product data");
        setProducts([]);
        setTotalPages(1);
      }
    } catch (error) {
       setLoading(false);
      console.error("API Fetch Error:", error);
      toast.error("Something went wrong while fetching products");
    } finally {
      setLoading(false);
    }
  };

  /** Debounced search handler */
  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      getApiData(query, 1);
    }, 500),
    []
  );

  /** Handle search input change */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  /** Delete record */
  const deleteRecord = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await getData(`product/delete-product/${id}`);
        if (res.status) {
          toast.success("Product deleted successfully");
          getApiData();
        } else {
          toast.error(res.message || "Failed to delete product");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error while deleting product");
      }
    }
  };

  /** Load data on page or search change */
  useEffect(() => {
    getApiData();
  }, [currentPage]);

  return (
    <>
      <ToastContainer />

      {/* Header */}
      <div className="bread">
        <div className="head">
          <h4>All Product List</h4>
        </div>
        <div className="links">
          <Link to="/add-product" className="add-new">
            Add New <i className="fa-solid fa-plus"></i>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="filteration">
        <div className="search">
          <label htmlFor="search">Search </label> &nbsp;
          <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Search products..." />
        </div>
      </div>

      {/* Table */}
      <section className="d-table">
        {loading ? (
          <p className="text-center">Loading...</p>
        ) : (
          <table className="table table-bordered table-striped table-hover">
            <thead>
              <tr>
                <th>Sr.No.</th>
                <th style={{ width: "300px" }}>Product Name</th>
                <th>Brand Name</th>
                <th>Part No.</th>
                <th>Image</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {products.length > 0 ? (
                products.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.brand_name}</td>
                    <td>{item.part_no}</td>
                    <td>
                      <img
                        src={
                          item?.image
                            ? item?.image.includes("uploads/product")
                              ? `${serverURL}/${item?.image}`
                              : `${serverURL}/uploads/product/${item?.image}`
                            : defaultImage
                        }
                        alt="Product"
                        style={{ height: 50 }}
                      />
                    </td>
                    <td>
                      <Link
                        className="bt edit"
                        to={`/edit-product/${item?.id}`}
                      >
                        Edit <i className="fa-solid fa-pen-to-square"></i>
                      </Link>
                    </td>
                    <td>
                      <button
                        className="bt delete"
                        onClick={() => deleteRecord(item?.id)}
                      >
                        Delete <i className="fa-solid fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
      </section>
    </>
  );
};

/** Pagination Component */
const Pagination = ({ totalPages, currentPage, onPageChange }) => {
  const getPaginationNumbers = () => {
    const maxPages = 5;
    const pages = [];
    if (totalPages <= maxPages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, currentPage + 2);
      if (start > 1) pages.push(1, "...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages) pages.push("...", totalPages);
    }
    return pages;
  };

  return (
    <nav>
      <ul className="pagination justify-content-center">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            className="page-link"
          >
            Previous
          </button>
        </li>
        {getPaginationNumbers().map((num, idx) =>
          typeof num === "number" ? (
            <li key={idx} className="page-item">
              <button
                className={`page-link ${currentPage === num ? "active" : ""
                  }`}
                onClick={() => onPageChange(num)}
              >
                {num}
              </button>
            </li>
          ) : (
            <li key={idx} className="page-item disabled">
              <span className="page-link">...</span>
            </li>
          )
        )}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
        >
          <button
            onClick={() => onPageChange(currentPage + 1)}
            className="page-link"
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default AllProduct;
